import { Alignment, Button, Classes, Dialog, FormGroup, Icon, InputGroup } from "@blueprintjs/core";
import { MDBBtn, MDBCol, MDBRow } from "mdbreact";
import { observable } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import { AppStore } from "../../stores/AppStore";
import { BootstrapTable, TableHeaderColumn } from "react-bootstrap-table";
import { copyToClipboard, formatCnpj, formatCpfOrCnpj } from "../../utils";
import { Pager } from "../utils/Pager";
import { getTag } from "../utils/getStatus";
import { NfeFazendaStore } from "../../stores/nfe-fazenda/NfeFazendaStore";
import moment from "moment";
import { EmpresaSelect, filterEmpresa, renderEmpresa } from "../empresa/EmpresaSelect";
import { EmpresaStore } from "../../stores/empresa/EmpresaStore";
import { IEmpresa, newEmpresa } from "../../model";
import { IconButton, Table, TableCell, TableHead, TableRow } from "@mui/material";
import { toast } from "react-toastify";
import InfiniteScroll from "react-infinite-scroll-component";
import { AppIcon } from "../utils/AppIcon";
import { ResultadoStore } from "../../stores/resultados/ResultadoStore";
import dayjs from "dayjs";
import { ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";

/**
 * Página de consulta e gerenciamento de tarefas da NF-e no portal da Fazenda.
 * 
 * Esta tela permite:
 * - Selecionar uma empresa para buscar tarefas NF-e.
 * - Listar tarefas processadas ou pendentes no portal.
 * - Visualizar erros retornados pelo portal.
 * - Consultar e exibir as chaves NF-e encontradas.
 * - Paginar resultados e carregar mais dados sob demanda.
 * 
 * @observer - Decorator MobX para reatividade da UI.
 */
@observer
export class NfeFazendaPage extends React.Component<{}, {}> {

  /**
   * Estado observável da página.
   */
  obs = observable({
    selectEmpresa: newEmpresa() as IEmpresa,
    current_page: 1 as number,
    erros_relatorio: [] as any,

    task_id: "" as string,
    current_page_chaves: 1 as number,
    open_details: false as boolean,
    itens: [],
    loading: false as boolean,
    searchChave: "" as string
  })

  /**
   * Construtor da página.
   * Define o título no AppStore.
   * 
   * @param props Propriedades do componente React
   */
  constructor(props: any) {
    super(props);
    AppStore.setTitle("Portal da NF-e");
  }

  /**
   * Ciclo de vida React.
   * Ao montar o componente, carrega a primeira página de tarefas.
   */
  async componentDidMount(): Promise<void> {

    await NfeFazendaStore.handleTasksPaged(1, "")

  }

  /**
   * Alterar a página atual de tarefas.
   * 
   * @param page Número da página a carregar
   */
  async handleChangePage(page: number) {

    this.obs.current_page = page

    await NfeFazendaStore.handleTasksPaged(this.obs.current_page, this.obs.selectEmpresa.cnpj)

  }

  /**
   * Exibe erros retornados pelo portal.
   * 
   * @param error Mensagem de erro recebida
   */
  handleErrosPortal(error: string) {

    if (!error) {
      this.obs.erros_relatorio = "Nenhum erro econtrado!"
    }

    this.obs.erros_relatorio = error

    NfeFazendaStore.obs.open_erros = true
  }

  /**
   * Fecha o modal de erros e limpa a lista.
   */
  onCloseErros() {

    NfeFazendaStore.obs.open_erros = false

    this.obs.erros_relatorio = []

  }

  /**
   * Seleciona a empresa e busca tarefas associadas.
   * 
   * @param empresa Empresa selecionada
   */
  handleBuscaTarefas = async (empresa) => {

    this.obs.selectEmpresa = empresa

    await NfeFazendaStore.handleTasksPaged(1, this.obs.selectEmpresa.cnpj);

  };

  /**
   * Busca chaves NF-e para uma tarefa específica.
   * 
   * @param row Objeto da tarefa
   */
  async handleBuscaChaves(row) {

    this.obs.itens = []
    this.obs.current_page_chaves = 1
    this.obs.task_id = row.task_id
    this.obs.searchChave = ""

    this.obs.loading = true

    let resp = await NfeFazendaStore.handleBuscaChaves(1, row.task_id, "")

    this.obs.itens = resp

    this.obs.loading = false
    this.obs.open_details = true

  }

  /**
   * Carrega mais chaves NF-e na rolagem infinita.
   */
  fetchMoreAvisos = async () => {

    this.obs.current_page_chaves = this.obs.current_page_chaves + 1

    let resp = await NfeFazendaStore.handleBuscaChaves(this.obs.current_page_chaves, this.obs.task_id, this.obs.searchChave)

    this.obs.itens = [...this.obs.itens, ...resp]

  };

  /**
   * Fecha o modal de detalhes das chaves e reseta o estado.
   */
  onClose() {

    this.obs.open_details = false

    this.obs.itens = []
    this.obs.current_page_chaves = 1
    this.obs.task_id = ""
  }

  /**
   * Copia uma chave NF-e para a área de transferência.
   * 
   * @param token Chave NF-e
   */
  copyChave(token: string) {

    copyToClipboard(token);

    toast.success('Chave de copiada', {});

  }

  /**
   * Copia uma chave NF-e para a área de transferência.
   * 
   * @param token Chave NF-e
   */
  statusFormatter(cell, row) {

    return getTag(row.status, "nfe_fazenda");

  }

  /**
   * Formata o botão para exibir erros.
   */
  errosFormatter = (cell, row) => {
    return (
      <>
        <MDBBtn
          color="default"
          rounded
          size="sm"
          disabled={!(row.status == 3 || row.status == 9 || row.status == 4)}
          onClick={() => this.handleErrosPortal(row.error)}
        >
          <img src="/images/view.png" />
        </MDBBtn>
      </>
    );
  }

  /**
   * Formata a data de criação adicionando 3 horas e exibindo no padrão DD/MM/YYYY HH:mm.
   */
  dateFormatter = (cell, row) => {

    return moment(cell).add(3, "hour").format("DD/MM/YYYY") + " ás " + moment(cell).add(3, "hour").format("HH:mm");

  };

  /**
   * Formata o CNPJ.
   */
  cnpjFormatter = (cell, row) => {

    return formatCpfOrCnpj(row.cnpj)

  };

  /**
   * Formata o texto sobre quantidade de chaves encontradas.
   */
  chavesFormatter = (cell, row) => {

    if (cell == 0) return (<b>Nenhuma chave encontrada.</b>)

    if (!cell) return ""

    if (cell == 1) return (<b>1 chave foi encontrada.</b>)

    return (<b>{cell} chaves foram encontradas.</b>)

  }

  /**
   * Exibe ícone de busca para detalhes das chaves, se houver chaves.
   */
  detailChavesFormatter = (cell, row) => {

    if (row.total_chaves == 0) return ""

    if (!row.total_chaves) return ""

    return (<AppIcon size={22} name="search" style={{ color: "#3579ea", cursor: "pointer" }} onClick={() => this.handleBuscaChaves(row)} />)

  }

  handleViewXML = async (row: any) => {

    const dhemi = dayjs(row.dhemi).add(3, "hours").format("YYYY-MM-DDTHH:mm:ssZ");

    await NfeFazendaStore.download_xml(row.chave, "Entrada", "NF-E", dhemi, row.cnpj);

  };

  handleViewPDF = async (row: any) => {

    const dhemi = dayjs(row.dhemi).add(3, "hours");

    var ids = {
      ids: [row.nota_id],
      filtro: "Entrada",
      tipo: "NF-E",
      empresa: row.cnpj,
      data1: dhemi.startOf('month').format("YYYY-MM-DD"),
      data2: dhemi.endOf('month').format("YYYY-MM-DD"),
    };

    await NfeFazendaStore.view_danfe(ids, row.chave);

  };

  /**
   * Carrega mais chaves NF-e na rolagem infinita.
   */
  resetBusca = async () => {

    this.obs.loading = true

    this.obs.current_page_chaves = 1

    let resp = await NfeFazendaStore.handleBuscaChaves(1, this.obs.task_id, this.obs.searchChave)

    this.obs.itens = []
    this.obs.itens = resp

    this.obs.loading = false

  };

  async handleFilter(filter: string) {

    this.obs.loading = true

    this.obs.current_page_chaves = 1
    this.obs.searchChave = filter

    let resp = await NfeFazendaStore.handleBuscaChaves(1, this.obs.task_id, filter)

    this.obs.itens = []
    this.obs.itens = resp

    this.obs.loading = false

  }

  /**
   * Renderiza a interface da página.
   */
  render() {

    let items = NfeFazendaStore.obs.tasks_portal?.map(imp => {

      return ({
        id: imp._id,
        cnpj: imp.cnpj,
        razao_social: imp.razao_social,
        total_chaves: imp.total_chaves,
        inserted_at: imp.inserted_at,
        status: imp.status,
        task_id: imp.task_id,
        error: imp.error,
      });

    });

    let empresaText = this.obs.selectEmpresa.cnpj != ""
      ? `${this.obs.selectEmpresa.razao_social.substring(0, 38)}... (${formatCnpj(this.obs.selectEmpresa.cnpj)})`
      : "Selecione uma empresa";

    const ClassificacaoCell = ({ row, handlePDF, handleXML }) => {
      const [anchorEl, setAnchorEl] = React.useState(null);
      const open = Boolean(anchorEl);

      const handleClose = () => {
        setAnchorEl(null);
      };

      const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
      };

      return (<div>
        <IconButton onClick={handleClick}><img src="/images/download.svg" width={20} /></IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          <MenuItem onClick={() => handlePDF(row)}>
            <ListItemIcon><img src="/images/danfe.svg" width={22}/></ListItemIcon>
            <ListItemText>DANFE em PDF</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => handleXML(row)}>
            <ListItemIcon><img src="/images/xml.svg" width={22}/></ListItemIcon>
            <ListItemText>Download XML</ListItemText>
          </MenuItem>
        </Menu>
      </div>);
    };

    const errosChavesFormatter = (row) => {

      if (row.status == 2) return (<ClassificacaoCell row={row} handlePDF={this.handleViewPDF.bind(this, row)} handleXML={this.handleViewXML.bind(this, row)} />)

      if (!row.error) return null

      return (
        <>
          <MDBBtn
            color="default"
            rounded
            size="sm"
            disabled={!(row.status == 3 || row.status == 9 || row.status == 4)}
            onClick={() => this.handleErrosPortal(row.error)}
          >
            <img src="/images/view.png" />
          </MDBBtn>
        </>
      );
    }

    return (
      <>

        <MDBRow>

          <MDBCol md="4">
            <FormGroup className="empresaSelect" label="Empresa">
              <EmpresaSelect
                items={EmpresaStore.sortedEmpresas}
                itemRenderer={renderEmpresa}
                itemPredicate={filterEmpresa}
                onItemSelect={(e: IEmpresa) => this.handleBuscaTarefas(e)}
                inputProps={{ placeholder: "Filtrar uma empresa" }}
              >
                <Button
                  text={empresaText || "Selecione uma empresa"}
                  alignText={Alignment.LEFT}
                  rightIcon={
                    this.obs.selectEmpresa.cnpj ? (
                      <Icon
                        className="clear-icon-empresa"
                        icon="cross"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          this.handleBuscaTarefas(newEmpresa());
                        }}
                      />
                    ) : (
                      <Icon icon="double-caret-vertical" style={{ padding: 5 }} />
                    )
                  }
                />
              </EmpresaSelect>
            </FormGroup>
          </MDBCol>

          <MDBCol md="2">
            <MDBBtn className="btnDefault btnBuscarNfe mt25" onClick={() => this.handleChangePage(1)}>
              Buscar
            </MDBBtn>
          </MDBCol>

        </MDBRow>

        <div data-tut="table">
          <BootstrapTable className="TableDefault" ref="table" data={items}>
            <TableHeaderColumn dataField="id" isKey={true} hidden width="1%">#</TableHeaderColumn>
            <TableHeaderColumn dataField="razao_social" width="30%">Razão Social</TableHeaderColumn>
            <TableHeaderColumn dataFormat={this.cnpjFormatter} dataField="cnpj" width="20%">CNPJ</TableHeaderColumn>
            <TableHeaderColumn dataFormat={this.dateFormatter} dataField="inserted_at" width="20%">Criado em:</TableHeaderColumn>
            <TableHeaderColumn dataFormat={this.chavesFormatter} dataField="total_chaves" width="30%">Total Chaves</TableHeaderColumn>
            <TableHeaderColumn dataField="status" dataFormat={this.statusFormatter} width="30%">Status</TableHeaderColumn>
            <TableHeaderColumn dataFormat={this.detailChavesFormatter} width="10%">Detalhes</TableHeaderColumn>
            <TableHeaderColumn dataFormat={this.errosFormatter} width="10%">Erros</TableHeaderColumn>
          </BootstrapTable>
        </div>

        <div style={{ marginTop: 5, float: "right", marginBottom: 120 }}>
          <Pager
            currentPage={this.obs.current_page}
            totalCount={NfeFazendaStore.obs.length_tasks_portal}
            pageSize={15}
            onPageChange={page => this.handleChangePage(page)}
          />
        </div>

        <Dialog
          isOpen={NfeFazendaStore.obs.open_erros}
          onClose={() => this.onCloseErros()}
          className="AlertErrorSped"
          title="Logs Erros"
        >
          <div className={Classes.DIALOG_BODY}>
            <p>{this.obs.erros_relatorio}</p>
          </div>

          <div className={Classes.DIALOG_FOOTER}>
            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
              <Button text="Fechar" intent="primary" onClick={() => this.onCloseErros()} />
            </div>
          </div>

        </Dialog>

        <Dialog
          isOpen={this.obs.open_details}
          onClose={() => this.onClose()}
        >
          <div className="flash-error">
            <div style={{ textAlign: "center", marginTop: 0 }}>
              <h3 style={{ color: "#3579ea" }}>Chaves Encontradas <IconButton onClick={() => this.resetBusca()}><AppIcon name="undo" color="#3579ea" size={24} /></IconButton></h3>
            </div>

            <div style={{ padding: 5, border: "1px solid #dee2e6", borderRadius: 10 }}>
              <InputGroup
                value={this.obs.searchChave}
                onChange={(e) => this.handleFilter(e.target.value)}
                type="text"
                placeholder="Buscar por chave"
                style={{ marginBottom: "10px", width: "100%", padding: "5px", border: "1px solid #dee2e6" }}
              />

              <InfiniteScroll
                dataLength={this.obs.itens.length}
                next={this.fetchMoreAvisos}
                hasMore={true}
                height={350}
                loader={this.obs.loading ?
                  <div style={{ textAlign: "center" }}>
                    <div className="spinner-border" role="status">
                      <span className="sr-only"></span>
                    </div>
                  </div>
                  : ""}
              >
                <Table className="table align-middle" size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell width={300} style={{ fontWeight: "bold" }}>Chave</TableCell>
                      <TableCell width={150} style={{ fontWeight: "bold" }}>Data Emissão</TableCell>
                      <TableCell width={150} style={{ fontWeight: "bold" }}>Situação NFe</TableCell>
                      <TableCell width={200} style={{ fontWeight: "bold" }}>Status</TableCell>
                      <TableCell width={150} style={{ fontWeight: "bold" }}>Data Atualização</TableCell>
                      <TableCell width={150} style={{ fontWeight: "bold" }} align="center">Detalhes</TableCell>
                    </TableRow>
                  </TableHead>
                  {this.obs.itens?.map((row) => {
                    return (<>
                      <tr key={row._id}>
                        <td>{row.chave}</td>
                        <td>{moment(row.dhemi).add(3, "hours").format("DD/MM/YYYY")} {moment(row.dhemi).add(3, "hours").format("HH:mm")} </td>
                        <td>{row.status_nfe}</td>
                        <td>{getTag(row.status, "manifestacao_chaves")}</td>
                        <td>{row._modifiedTS ? (`${moment(row._modifiedTS).add(3, "hours").format("DD/MM/YYYY")} ${moment(row._modifiedTS).add(3, "hours").format("HH:mm")}`) : "Sem atualização"}</td>
                        <td align="center">{errosChavesFormatter(row)}</td>
                      </tr>
                    </>)
                  })}
                </Table>
              </InfiniteScroll>
            </div>

          </div>

          <div style={{ textAlign: "center", marginTop: 10 }}>
            <Button text="Fechar" intent="primary" onClick={() => this.onClose()} />
          </div>

        </Dialog>

      </>
    )
  }
}

export default NfeFazendaPage;
